import { AssetCache, type AssetSession } from './asset-cache';

export interface AssetUrlLease { url: string; release(): void }
interface Generation { refs: number; controller: AbortController; promise: Promise<AssetSession> }
const generations = new Map<string, Generation>();
const cache = new AssetCache();
export function releaseAssetCacheStats() { return cache.stats; }
function aborted(): DOMException { return new DOMException('Asset request cancelled', 'AbortError'); }
function assertActive(signal?: AbortSignal): void { if (signal?.aborted) throw aborted(); }

/** Root-relative public AUTO-268 namespace; query strings never select a generation. */
export function releaseAssetPath(url: string): { root: string; path: string } | undefined {
  const parsed = new URL(url, typeof location === 'undefined' ? 'http://localhost/' : location.href);
  const match = /^\/releases\/([a-f0-9]{40})\/(.*)$/.exec(parsed.pathname);
  if (!match) {
    if (parsed.pathname.startsWith('/releases/')) throw Error('Invalid release asset namespace');
    return undefined;
  }
  if (typeof location !== 'undefined' && parsed.origin !== location.origin) throw Error('Foreign release asset origin');
  return { root: `/releases/${match[1]}/`, path: match[2]! };
}
function holdGeneration(root: string): { promise: Promise<AssetSession>; release(): void } {
  let generation = generations.get(root);
  if (!generation) {
    const controller = new AbortController();
    generation = { refs: 0, controller, promise: undefined! };
    generation.promise = fetch(root + 'inventory.json', { signal: controller.signal }).then(async response => {
      if (!response.ok) throw Error(`Release inventory unavailable: ${response.status}; reload required`);
      const session = cache.pinInventory(await response.json());
      if (root !== `/releases/${session.inventory.sourceSha}/`) { session.release(); throw Error('Release inventory source mismatch'); }
      if (controller.signal.aborted) { session.release(); throw aborted(); }
      return session;
    });
    generations.set(root, generation);
  }
  generation.refs++;
  const held = generation;
  let released = false;
  return { promise: held.promise, release() {
    if (released) return; released = true;
    if (--held.refs === 0) {
      if (generations.get(root) === held) generations.delete(root);
      held.controller.abort();
      void held.promise.then(session => session.release(), () => undefined);
    }
  } };
}
function waitFor<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  assertActive(signal);
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const abort = () => reject(aborted());
    signal.addEventListener('abort', abort, { once: true });
    void promise.then(value => { signal.removeEventListener('abort', abort); resolve(value); }, error => { signal.removeEventListener('abort', abort); reject(error); });
  });
}
export async function readReleaseManifest(url: string, signal?: AbortSignal): Promise<{ value: unknown; manifestVersion: number; paths: ReadonlySet<string>; root: string; release(): void } | undefined> {
  assertActive(signal);
  const asset = releaseAssetPath(url);
  if (!asset) return undefined;
  const held = holdGeneration(asset.root);
  try {
    const session = await waitFor(held.promise, signal);
    // A frontend/admin shell may contain a public copy; the shared game manifest is canonical.
    const blob = await session.read('game/manifest.json', { signal });
    assertActive(signal);
    const value: unknown = JSON.parse(await blob.text());
    assertActive(signal);
    return { value, manifestVersion: session.inventory.manifestVersion, paths: new Set(session.inventory.entries.map(entry => entry.path)), root: asset.root, release: held.release };
  } catch (error) { held.release(); throw error; }
}
/** Decode consumers own this temporary URL until their source unload completes. */
export async function acquireVerifiedAssetUrl(url: string, signal?: AbortSignal): Promise<AssetUrlLease> {
  assertActive(signal);
  const asset = releaseAssetPath(url);
  if (!asset) return { url, release() {} };
  const held = holdGeneration(asset.root);
  try {
    const session = await waitFor(held.promise, signal);
    const blob = await session.read(asset.path, { signal });
    assertActive(signal);
    const objectUrl = URL.createObjectURL(blob);
    let released = false;
    return { url: objectUrl, release() {
      if (released) return; released = true;
      URL.revokeObjectURL(objectUrl); held.release();
    } };
  } catch (error) { held.release(); throw error; }
}
